<template>
  <main class="guests-page">
    <div class="guests-page__head">
      <div>
        <h1 class="guests-page__title">Archivio ospiti</h1>
        <p class="guests-page__subtitle">
          Elenco trasversale degli ospiti di tutte le strutture
        </p>
      </div>
      <router-link to="/guests/create" class="villa-btn villa-btn--primary">
        <i class="fa-solid fa-plus"></i> Nuovo ospite
      </router-link>
    </div>

    <div class="guests-table-wrap">
      <table class="guests-table">
        <thead>
          <tr>
            <th>Ospite</th>
            <th>Struttura</th>
            <th>Data di nascita</th>
            <th>Città</th>
            <th>Retta mensile</th>
            <th>Stato</th>
            <th class="actions-cell">Azioni</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="isLoading">
            <td colspan="7" class="no-result">Caricamento…</td>
          </tr>

          <template v-else>
            <tr
              v-for="guest in guests"
              :key="guest.id"
              @click="goToDetail(guest.id)"
            >
              <td>{{ guest.lastName }} {{ guest.firstName }}</td>
              <td>
                <template v-if="guest.facility">
                  {{ guest.facility.name }}
                </template>
                <span v-else class="guest-no-facility">Nessuna struttura</span>
              </td>
              <td>{{ formatDate(guest.birthDate) }}</td>
              <td>{{ guest.city || "—" }}</td>
              <td>{{ feeLabel(guest.monthlyFee) }}</td>
              <td>
                <span
                  :class="['status-badge', 'status-badge--' + guest.status]"
                >
                  {{ statusLabel(guest.status) }}
                </span>
              </td>
              <td class="actions-cell" @click.stop>
                <button
                  class="icon-btn"
                  title="Dettaglio"
                  @click="goToDetail(guest.id)"
                >
                  <i class="fa-solid fa-eye"></i>
                </button>
                <button
                  class="icon-btn"
                  title="Modifica"
                  @click="goToEdit(guest.id)"
                >
                  <i class="fa-solid fa-pen-to-square"></i>
                </button>
                <button
                  class="icon-btn icon-btn--danger"
                  title="Archivia"
                  @click="openArchiveModal(guest)"
                >
                  <i class="fa-solid fa-trash-can"></i>
                </button>
              </td>
            </tr>

            <tr v-if="guests.length === 0">
              <td colspan="7" class="no-result">Nessun ospite trovato.</td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <div class="guests-bottom">
      <div class="search-container">
        <i
          v-if="!searchTerm"
          class="fa-solid fa-magnifying-glass search-icon"
        ></i>
        <input
          v-model="searchTerm"
          type="text"
          class="search-box"
          placeholder="Cerca ospiti…"
        />
        <button
          v-if="searchTerm"
          class="clear-search-button"
          @click="resetSearch"
        >
          <i class="fa-solid fa-times"></i>
        </button>
      </div>

      <div class="pagination-controls">
        <span class="pagination-info">
          {{ pagination.startItem || 0 }}-{{ pagination.endItem || 0 }} di
          {{ pagination.totalItems || 0 }}
        </span>
        <button
          class="pages-button"
          :disabled="pagination.currentPage <= 1"
          @click="goToPage(pagination.currentPage - 1)"
        >
          <i class="fa-solid fa-chevron-left"></i>
        </button>
        <button
          v-for="pageNum in visiblePages"
          :key="pageNum"
          class="pages-button"
          :class="{ active: pagination.currentPage === pageNum }"
          @click="goToPage(pageNum)"
        >
          {{ pageNum }}
        </button>
        <button
          class="pages-button"
          :disabled="pagination.currentPage >= pagination.totalPages"
          @click="goToPage(pagination.currentPage + 1)"
        >
          <i class="fa-solid fa-chevron-right"></i>
        </button>
      </div>
    </div>

    <!-- Modale conferma archiviazione -->
    <VueFinalModal
      v-model="showArchiveModal"
      :clickToClose="!archiving"
      :escToClose="!archiving"
      content-class="villa-confirm"
    >
      <h3 class="villa-confirm__title">Archivia ospite</h3>
      <p class="villa-confirm__text">
        Sei sicuro di voler archiviare
        <strong
          >{{ selectedGuest?.firstName }} {{ selectedGuest?.lastName }}</strong
        >?
      </p>
      <div class="villa-confirm__actions">
        <button
          class="villa-btn villa-btn--ghost"
          :disabled="archiving"
          @click="showArchiveModal = false"
        >
          Annulla
        </button>
        <button
          class="villa-btn villa-btn--danger"
          :disabled="archiving"
          @click="confirmArchive"
        >
          {{ archiving ? "Archivia…" : "Archivia" }}
        </button>
      </div>
    </VueFinalModal>
  </main>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

export default {
  name: "GuestsPage",
  data() {
    return {
      isLoading: false,
      searchTerm: "",
      searchTimer: null,
      showArchiveModal: false,
      archiving: false,
      selectedGuest: null,
    };
  },
  computed: {
    ...mapGetters({
      guests: "guests/list",
      pagination: "guests/pagination",
    }),
    visiblePages() {
      const range = 2;
      const current = this.pagination.currentPage || 1;
      const total = this.pagination.totalPages || 1;
      const start = Math.max(1, current - range);
      const end = Math.min(total, current + range);
      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    },
  },
  methods: {
    ...mapActions(["fetchGuests", "archiveGuest"]),

    statusLabel(status) {
      return (
        {
          active: "Attivo",
          discharged: "Dimesso",
          suspended: "Sospeso",
          deceased: "Deceduto",
        }[status] || status
      );
    },
    formatDate(d) {
      if (!d) return "—";
      const [y, m, day] = String(d).slice(0, 10).split("-");
      return day && m && y ? `${day}/${m}/${y}` : d;
    },
    feeLabel(v) {
      if (v === null || v === undefined || v === "") return "—";
      const n = Number(v);
      if (!Number.isFinite(n)) return "—";
      return `€ ${n.toLocaleString("it-IT", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} / mese`;
    },

    async loadGuests(page = 1) {
      this.isLoading = true;
      try {
        await this.fetchGuests({ page, limit: 8, search: this.searchTerm });
      } catch (err) {
        if (err?.response?.status !== 404) {
          this.$toast.error(
            err?.response?.data?.error || "Errore nel caricamento degli ospiti."
          );
        }
      } finally {
        this.isLoading = false;
      }
    },
    goToPage(page) {
      if (page < 1 || page > (this.pagination.totalPages || 1)) return;
      this.loadGuests(page);
    },
    resetSearch() {
      this.searchTerm = "";
    },

    goToDetail(id) {
      this.$router.push({ name: "guest-detail", params: { id } });
    },
    goToEdit(id) {
      this.$router.push({ name: "edit-guest", params: { id } });
    },

    openArchiveModal(guest) {
      this.selectedGuest = guest;
      this.showArchiveModal = true;
    },
    async confirmArchive() {
      if (!this.selectedGuest) return;
      this.archiving = true;
      try {
        await this.archiveGuest(this.selectedGuest.id);
        this.$toast.success("Ospite archiviato con successo.");
        this.showArchiveModal = false;
        this.selectedGuest = null;
        this.loadGuests(1);
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.error || "Impossibile archiviare l'ospite."
        );
      } finally {
        this.archiving = false;
      }
    },
  },
  watch: {
    searchTerm() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.loadGuests(1), 300);
    },
  },
  mounted() {
    this.loadGuests(1);
  },
};
</script>

<style src="@/css/GuestsPage.css" />
